import { useCallback } from 'react';

import { getAppInsights } from 'analytics/appInsights';
import { pathJoin } from 'utils/strings/pathJoin';

import { getAccessToken } from './authentication/utils/getAccessToken';
import type { Fetcher } from './fetcher';
import { fetchWithToken } from './fetcher';

/**
 * Hook that returns the fetcher function with access token and error logger.
 * The signature of the returned fetcher function is identical to native window.fetch function.
 */
export const useApi = (baseUrl: string, errorKey: string): Fetcher => {
  return useCallback(
    async <T>(url: string, params?: RequestInit) => {
      const accessToken = getAccessToken();
      const response = await fetchWithToken<T>(pathJoin(baseUrl, url), accessToken, params);
      // A request the caller cancelled (e.g. React Query on unmount) isn't an error worth reporting.
      if (!response.success && !params?.signal?.aborted) {
        appInsightLogger(errorKey, response.error);
      }
      return response;
    },
    [baseUrl, errorKey]
  );
};

export const appInsightLogger = (clientPrefix: string, error: string) => {
  const appInsights = getAppInsights();
  appInsights.trackException({ exception: new Error(`${clientPrefix} ::: ${error}`) });
};
